import { TagChip } from '@/components/tags/TagChip';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { gql } from '@apollo/client';

// The tags subquery is small and this fragment is spread unconditionally on the shared
// visit fragments / dashboard queries; rendering is what's flag-gated (per surface), so we
// intentionally don't @include-gate the fetch except on the Patients page query.
export const PatientTagsFragment = gql`
    fragment PatientTags on Patient {
        tags(filters: { isActive: { exact: true } }) {
            items {
                id
                name
            }
        }
    }
`;

interface PatientTag {
    id: string;
    name: string;
}

interface PatientTagsProps {
    tags: PatientTag[];
    // Fill the parent's width so long chip labels wrap against a bounded container
    // (rather than the chip row shrinking to its content and overflowing the cell).
    fullWidth?: boolean;
}

// Keep each chip on a single line; ellipsis a long label rather than wrapping it taller.
const TRUNCATING_CHIP_SX = {
    maxWidth: '100%',
    '& .MuiChip-label': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
} as const;

export const PatientTags = ({ tags, fullWidth = false }: PatientTagsProps) => {
    if (tags.length === 0) {
        return null;
    }
    return (
        <HorizontalStack
            spacing={0.5}
            fullWidth={fullWidth}
            flexWrap='nowrap'
            useFlexGap
            alignItems='center'
            sx={{ minWidth: 0, overflow: 'hidden' }}
        >
            {tags.map((tag) => (
                <TagChip key={tag.id} label={tag.name} size='small' sx={TRUNCATING_CHIP_SX} />
            ))}
        </HorizontalStack>
    );
};

/**
 * Renders a patient's name with their team tag chips stacked underneath. Chips only appear
 * when `showTags` is true (feature-flag gated by the caller). Used in table Patient columns.
 */
export const PatientNameWithTags = ({
    fullName,
    tags,
    showTags,
}: {
    fullName: string;
    tags: { items: PatientTag[] } | null | undefined;
    showTags: boolean;
}) => {
    if (!showTags) {
        return <>{fullName}</>;
    }
    return (
        <VerticalStack spacing={0.25}>
            <span>{fullName}</span>
            <PatientTags tags={tags?.items ?? []} fullWidth />
        </VerticalStack>
    );
};
